
import { useEffect } from "react";
import { Link } from "react-router-dom";
import ImageUploader from "@/components/ImageUploader";
import NavBar from "@/components/NavBar";
import SEOContent from "@/components/SEOContent";
import SEOHead from "@/components/SEOHead";
import BreadcrumbSEO from "@/components/BreadcrumbSEO";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";

const Index = () => {
  const { checkSubscription, isPremium } = useSubscription();
  const { toast } = useToast();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const section = params.get('section');
    if (section) {
      setTimeout(() => {
        const el = document.getElementById(section);
        if (el) {
          const navbarHeight = 64;
          window.scrollTo({ top: el.offsetTop - navbarHeight - 20, behavior: 'smooth' });
        }
        window.history.replaceState({}, '', '/');
      }, 300);
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');

    if (success) {
      toast({
        title: "¡Suscripción exitosa!",
        description: "Bienvenido a Premium. Disfrutar de todas las funciones.",
      });
      window.history.replaceState({}, document.title, "/");
      setTimeout(() => checkSubscription(), 2000);
    } else if (canceled) {
      toast({
        title: "Suscripción cancelada",
        description: "No se procesó el pago. Puedes intentar nuevamente.",
        variant: "destructive",
      });
      window.history.replaceState({}, document.title, "/");
    }
  }, [toast, checkSubscription]);

  const scrollToTool = () => {
    document.getElementById('tool-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const steps = [
    {
      number: "1",
      title: "Sube tus imágenes",
      description: "Arrastra y suelta tus imágenes JPEG o PNG, o haz clic para seleccionarlas"
    },
    {
      number: "2", 
      title: "Ajusta la calidad",
      description: "Configura el nivel de compresión y opciones de redimensionamiento"
    },
    {
      number: "3",
      title: "Descarga",
      description: "Descarga tus imágenes optimizadas individual o grupalmente"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <SEOHead 
        title="Compresor de Imágenes Online Gratis - JPG, PNG, WebP | CompresorJPEG"
        description="Comprime y optimiza imágenes JPG, PNG, WebP gratis. Reduce hasta 90% del tamaño sin perder calidad. Herramienta rápida, segura y sin instalación."
        keywords="comprimir imagenes, compresor jpg, comprimir png, optimizar webp, reducir tamaño imagen, comprimir fotos online, optimizar imagenes web"
        canonical="https://compresorjpeg.com/"
      />
      
      <NavBar />
      
      <div className="container mx-auto px-4">
        <BreadcrumbSEO />
      </div>
      
      {/* Hero Section with CTA */}
      <header className="text-center py-10 animate-fade-in">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-gray-800 mb-4 transform transition-all duration-700 hover:scale-105">
            Compresor de Imágenes Online Gratis - JPG, PNG, WebP
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed transition-opacity duration-500 hover:opacity-80 mb-6">
            Nuestra herramienta de <strong>compresión de imágenes</strong> te permite <strong>comprimir JPG, PNG, WebP y otros formatos</strong> de forma gratuita y rápida. 
            Ideal para <strong>optimizar imágenes</strong> para web, email o almacenamiento, manteniendo una excelente calidad visual.
          </p>
          
          <Button 
            size="lg" 
            onClick={scrollToTool}
            className="text-lg px-8 py-6 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 mb-6"
          >
            Comprimir imágenes ahora
          </Button>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto text-sm text-gray-600">
            <div className="flex items-center justify-center space-x-2 p-3 bg-white/50 rounded-lg">
              <span className="text-green-600 font-bold">✓</span>
              <span>Reduce hasta <strong>90% del tamaño</strong></span>
            </div>
            <div className="flex items-center justify-center space-x-2 p-3 bg-white/50 rounded-lg">
              <span className="text-green-600 font-bold">✓</span>
              <span><strong>100% gratuito</strong> y seguro</span>
            </div>
            <div className="flex items-center justify-center space-x-2 p-3 bg-white/50 rounded-lg">
              <span className="text-green-600 font-bold">✓</span>
              <span>Sin instalación requerida</span>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 pb-8">
        {/* Image Uploader - Main tool (NOW FIRST) */}
        <section id="tool-section" className="animate-fade-in mb-16" aria-label="Herramienta de compresión de imágenes">
          <h2 className="sr-only">Compresor de Imágenes Online</h2>
          <ImageUploader />
        </section>

        {/* How it works section (MOVED BELOW TOOL) */}
        <section id="how-it-works-section" className="text-center mb-16 animate-fade-in">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 transform transition-all duration-500 hover:scale-105">
            ¿Cómo comprimir imágenes online?
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-12 text-lg">
            Sigue estos 3 sencillos pasos para <strong>comprimir tus imágenes</strong> y <strong>optimizar el tamaño de archivo</strong> sin perder calidad visual:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {steps.map((step, index) => (
              <article 
                key={index} 
                className="text-center group transform transition-all duration-500 hover:scale-105"
              >
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold transition-all duration-300 group-hover:bg-blue-700 group-hover:shadow-xl group-hover:scale-110">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3 transition-colors duration-300 group-hover:text-blue-600">
                  {step.title}
                </h3>
                <p className="text-gray-600 transition-all duration-300 group-hover:text-gray-700 group-hover:transform group-hover:scale-105">
                  {step.description}
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* SEO Content */}
        <section id="ventajas-section" className="animate-fade-in mb-16">
          <SEOContent showOnlyFirstSections={true} />
        </section>

        <section id="caracteristicas-section" className="mb-16 animate-fade-in">
          <SEOContent showOnlyFirstSections={false} />
        </section>

        {/* Trust signals */}
        <section className="text-center py-12 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl mb-16" aria-label="Indicadores de confianza">
          <h2 className="text-2xl font-bold text-gray-800 mb-8">
            Más de 50,000 usuarios confían en nuestro compresor
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">50K+</div>
              <div className="text-sm text-gray-600">Usuarios activos</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">1M+</div>
              <div className="text-sm text-gray-600">Imágenes comprimidas</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">90%</div>
              <div className="text-sm text-gray-600">Reducción promedio</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600 mb-2">4.8★</div>
              <div className="text-sm text-gray-600">Valoración usuarios</div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-8">
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Formatos Soportados</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>Comprimir JPEG/JPG</li>
                <li>Optimizar PNG</li>
                <li>Convertir WebP</li>
                <li>Reducir GIF</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Casos de Uso</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>Optimización para web</li>
                <li>Redes sociales</li>
                <li>Email marketing</li>
                <li>Almacenamiento eficiente</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Ventajas SEO</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>Velocidad de carga mejorada</li>
                <li>Mejor Core Web Vitals</li>
                <li>Reducción de ancho de banda</li>
                <li>Experiencia de usuario superior</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Recursos</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>
                  <Link to="/faq" className="hover:text-blue-600 transition-colors duration-200">
                    Preguntas Frecuentes
                  </Link>
                </li>
                <li>
                  <Link to="/history" className="hover:text-blue-600 transition-colors duration-200">
                    Historial de Compresión
                  </Link>
                </li>
                <li>
                  <Link to="/quitar-fondo" className="hover:text-blue-600 transition-colors duration-200">
                    Quitar Fondo de Imagen
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="hover:text-blue-600 transition-colors duration-200">
                    Blog y Guías
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Legal</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>
                  <Link to="/privacidad" className="hover:text-blue-600 transition-colors duration-200">
                    Política de Privacidad
                  </Link>
                </li>
                <li>
                  <Link to="/terminos" className="hover:text-blue-600 transition-colors duration-200">
                    Términos de Uso
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="text-xs text-gray-500 border-t border-gray-200 pt-4">
            <p>© 2025 CompresorJPEG. Herramienta gratuita para comprimir imágenes online. Todos los derechos reservados.</p>
            <p className="mt-2">Comprime JPG, PNG, WebP y más formatos sin perder calidad. 100% gratuito y seguro.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
